'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Tune,
  PersonAdd,
  FactCheck,
  Dashboard,
  Approval,
  Business,
  Groups,
  Badge,
  Warning,
  Troubleshoot,
  DynamicForm,
  AssignmentInd,
  DonutLarge,
  VerifiedUser,
  EventRepeat,
  NotificationsActive,
  MobileFriendly,
  WarningAmber,
  ArrowForward,
  ExpandMore,
  Insights,
  Mail,
  AccountTree,
  CheckCircle,
  Assessment,
  FileDownload,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import '../features-responsive.css';

const BRAND_BLUE = '#153C7F';
const BRAND_GREEN = '#6BBE2D';

const workflowSteps = [
  {
    icon: Tune,
    title: 'Create & Configure',
    description: 'Build the assessment from configurable forms with a name, priority, and due date',
  },
  {
    icon: PersonAdd,
    title: 'Assign',
    description: 'Route the assessment to a specific user or an entire role at the facility',
  },
  {
    icon: FactCheck,
    title: 'Complete',
    description: 'Assignees work sections on web or mobile, saving drafts as they go',
  },
  {
    icon: Dashboard,
    title: 'Track & Review',
    description: 'Watch section-level completion on the dashboard and review submitted answers',
  },
  {
    icon: Approval,
    title: 'Attest',
    description: 'Leadership reviews and attests, with comments, creating a documented sign-off',
  },
];

const capabilities = [
  {
    icon: Business,
    title: 'Facility Profile & Identifiers',
    description: 'Document your CCN, NPI, facility type, certified beds, census, ownership and chain affiliation, staffing model, and emergency systems—with license and certification documents attached—so the assessment starts from an accurate operational picture.',
  },
  {
    icon: Groups,
    title: 'Resident Population & Acuity',
    description: 'Record census, admissions and discharges, age and gender distribution, length of stay, languages, and cultural, religious, and social support needs—so staffing and services decisions are grounded in who you actually care for.',
  },
  {
    icon: Badge,
    title: 'Staffing Analysis',
    description: 'Assess total staff, hours per resident day, shift coverage, ratios, contracted staff, licensure, training, competency gaps, turnover, and retention in one structured section that connects directly to your resident population data.',
  },
  {
    icon: Warning,
    title: 'Risk & Continuity of Operations',
    description: 'Evaluate structural hazards, equipment failures, infection risks, natural disasters, and utility disruptions—then document critical services, minimum staffing, backup power, water and food supplies, evacuation and shelter-in-place plans, and drill history.',
  },
  {
    icon: Troubleshoot,
    title: 'Gap Analysis & Mitigation',
    description: 'Identify gaps across resident needs, staffing, resources, services, and risk; prioritize them; and document mitigation plans, timelines, stakeholders, monitoring, and closure—so the assessment drives action instead of sitting in a binder.',
  },
  {
    icon: DynamicForm,
    title: 'Configurable Form Builder',
    description: 'Build and tailor assessment forms with the Form Wizard—no IT involvement. Use sections, instructions, and the question types you need, and preview the form before assigning it to staff.',
  },
  {
    icon: AssignmentInd,
    title: 'Assignment by User or Role',
    description: 'Assign each assessment to a specific person or an entire role with a priority, assigned date, and due date. Assignees see their work in dedicated Pending, In Progress, and Completed queues.',
  },
  {
    icon: DonutLarge,
    title: 'Real-Time Completion Tracking',
    description: 'The assessment dashboard shows section-level completion percentage, sections complete versus total, the last submitter, and last modified date for every assessment—so nothing stalls silently.',
  },
  {
    icon: VerifiedUser,
    title: 'Leadership Attestation',
    description: 'When the assessment is complete, leadership reviews the submitted answers and attests with a comment—creating a dated, attributable sign-off record you can point to during survey.',
  },
  {
    icon: EventRepeat,
    title: 'Recurring Cadence & Reviews',
    description: 'Configure quarterly or annual cadence and CareAscendium seamlessly generates the next assessment on schedule, with notifications—so annual reviews and interim updates don\'t depend on someone remembering.',
  },
  {
    icon: NotificationsActive,
    title: 'Due Items & Notifications',
    description: 'Assigned assessments surface on staff dashboards as due items with urgency indicators, and status changes trigger email, SMS, and push notifications—keeping the assessment moving without manual chasing.',
  },
  {
    icon: MobileFriendly,
    title: 'Mobile Access',
    description: 'Complete assessment domains from the CareAscendium mobile app with dedicated tabs for profile, resident population, staffing, resources, services, risk, continuity, evaluation, and gaps—wherever the work happens.',
  },
];

const integrations = [
  { icon: DynamicForm, title: 'Form Wizard', desc: 'Configure assessment forms and sections without IT involvement' },
  { icon: Insights, title: 'QAPI', desc: 'Evaluation and review planning fields tie assessment findings into your QAPI program' },
  { icon: NotificationsActive, title: 'Due Items', desc: 'Assigned assessments appear on staff daily task lists with urgency indicators' },
  { icon: Approval, title: 'Attestation', desc: 'Leadership sign-off captured as a dated, attributable record' },
  { icon: Mail, title: 'Notifications', desc: 'Email, SMS, and push alerts on assignment and status changes' },
  { icon: AccountTree, title: 'Multi-Facility Management', desc: 'Run and compare assessments across every facility you operate' },
];

const faqs = [
  {
    q: 'Does this cover what CMS requires in a facility assessment?',
    a: 'CareAscendium structures the assessment around the domains CMS expects—resident population, staffing, resources and services, risks, and continuity of operations—plus facility profile, evaluation planning, and gap analysis, with documents attached where needed.'
  },
  {
    q: 'Can different people complete different parts of the assessment?',
    a: 'Yes. Assessments are assigned to a specific user or an entire role, and assignees work from Pending, In Progress, and Completed queues with draft saving—so the DON, department heads, and administrators each handle their part.'
  },
  {
    q: 'How do we know how far along the assessment is?',
    a: 'The assessment dashboard shows completion percentage, sections complete versus total, the last submitter, and last modified date for every assessment at every facility.'
  },
  {
    q: 'How is leadership review documented?',
    a: 'Leadership reviews the submitted answers and attests directly in the system with a comment. The attestation is recorded with the attesting user and date, creating a sign-off trail for survey.'
  },
  {
    q: 'Can the assessment recur seamlessly?',
    a: 'Yes. Configure a quarterly or annual cadence and CareAscendium generates the next assessment on schedule, with notifications—so reviews don\'t depend on calendar reminders.'
  },
  {
    q: 'Can we customize the assessment forms?',
    a: 'Yes. Assessments are built on the Form Wizard, so you can configure sections, instructions, and question types to match your facility\'s needs—and preview the form before assigning it.'
  },
  {
    q: 'Does it work on mobile?',
    a: 'Yes. The CareAscendium mobile app includes dedicated tabs for every assessment domain—profile, resident population, staffing, resources, services, risk, continuity, evaluation, and gaps—on iOS and Android.'
  }
];

export default function FacilityAssessmentPage() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Facility Assessment Software for Skilled Nursing Facilities | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)',
          py: { xs: 8, md: 12 },
          px: 4,
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Facility Assessment"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto'
            }}
          >
            Complete Your CMS Facility Assessment—Every Domain, Every Gap, Every Sign-Off in One Place
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            CareAscendium gives skilled nursing facilities a structured, repeatable facility assessment workflow—from resident population and staffing through risk, continuity of operations, and gap analysis—with assignment, completion tracking, and leadership attestation on web and mobile.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>

          </Box>
        </Container>
      </Box>

      {/* Problem Section */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: 4, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', p: 2, borderRadius: '50%', bgcolor: 'rgba(245, 124, 0, 0.1)', mb: 3 }}>
              <WarningAmber sx={{ fontSize: 40, color: '#f57c00' }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 4, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge with Binder-Based Facility Assessments
            </Typography>
            <Typography sx={{ color: '#555', fontSize: '1.15rem', lineHeight: 1.8, textAlign: 'justify', mb: 4 }}>
              CMS requires every nursing facility to conduct and maintain a facility assessment that reflects its resident population, staffing, resources, and risks—and to keep it current. In most SNFs, that assessment lives in a Word document assembled once a year by one person, with no clear ownership, no progress visibility, and no proof of leadership review when surveyors ask for it.
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left', mb: 5 }}>
              {[
                "Facility assessment sections scattered across spreadsheets, Word docs, and email threads",
                "No clear assignment of who owns which section—or whether it was ever finished",
                "Resident population, staffing, and acuity data that's stale by the time the document is done",
                "Identified gaps with no mitigation plan, owner, timeline, or follow-up",
                "Annual and interim reviews that slip because nothing triggers them",
                "No leadership attestation trail to show surveyors the assessment was reviewed and approved"
              ].map((point, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Warning sx={{ color: '#d32f2f', mt: 0.5 }} />
                  <Typography sx={{ color: '#444', fontSize: '1.05rem' }}>{point}</Typography>
                </Box>
              ))}
            </Box>

            <Paper sx={{ p: 4, bgcolor: '#f8f9fa', borderLeft: `4px solid ${BRAND_BLUE}`, borderRadius: 2 }}>
              <Typography sx={{ fontStyle: 'italic', color: '#1a1a1a', fontSize: '1.1rem', fontWeight: 500 }}>
                "A facility assessment that doesn't match your actual residents, staffing, and risks isn't just out of date—it's a survey finding waiting to happen."
              </Typography>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Solution Section (Vertical Stack) */}
      <Box sx={{ pt: { xs: 10, md: 12 }, pb: { xs: 8, md: 10 }, px: 4, background: 'linear-gradient(168.42deg, rgba(95, 195, 0, 0.91) 4.18%, rgba(54, 102, 7, 0.91) 92.49%)' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 3, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
            One Workflow. From Assignment to Attestation.
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.9)', textAlign: 'center', maxWidth: 800, mx: 'auto', mb: 6, fontSize: '1.1rem', lineHeight: 1.6 }}>
            CareAscendium Facility Assessment turns the CMS facility assessment from a once-a-year document scramble into a managed, assignable workflow. Build the assessment from configurable forms, assign sections to the right people, track completion in real time, document gaps with mitigation plans, and close the loop with leadership attestation—all in one auditable record.
          </Typography>
          
          <Box sx={{ maxWidth: 800, mx: 'auto', position: 'relative' }}>
            {/* The Vertical Connecting Line */}
            <Box 
              sx={{ 
                position: 'absolute', 
                left: { xs: '38px', md: '48px' }, 
                top: '40px', 
                bottom: '40px', 
                width: '4px', 
                bgcolor: 'rgba(255,255,255,0.3)',
                zIndex: 1
              }} 
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 4, md: 5 } }}>
              {workflowSteps.map((step, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 }, position: 'relative', zIndex: 2 }}>
                  {/* Step Number / Icon */}
                  <Box
                    sx={{
                      width: { xs: 80, md: 100 },
                      height: { xs: 80, md: 100 },
                      borderRadius: '50%',
                      bgcolor: 'white',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      border: `4px solid ${BRAND_BLUE}`
                    }}
                  >
                    <step.icon sx={{ color: BRAND_BLUE, fontSize: { xs: 30, md: 36 } }} />
                  </Box>

                  {/* Card Content */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, md: 4 },
                      flexGrow: 1,
                      borderRadius: 4,
                      bgcolor: 'white',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.2)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '6px',
                        bgcolor: BRAND_BLUE
                      }
                    }}
                  >
                    <Typography variant="overline" sx={{ color: BRAND_GREEN, fontWeight: 800, fontSize: '0.85rem', letterSpacing: 1.5, display: 'block', mb: 0.5 }}>
                      STEP 0{i + 1}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, color: '#1a1a1a', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ color: '#555', lineHeight: 1.6, fontSize: { xs: '0.95rem', md: '1.05rem' } }}>
                      {step.description}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Key Capabilities */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 4, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 6, textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 2, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              CMS-Aligned Assessment Domains
            </Typography>
            <Typography sx={{ color: '#555', fontSize: '1.1rem', lineHeight: 1.6 }}>
              Capture the full scope CMS expects in a facility assessment—facility profile, resident population, staffing, resources, services offered, risk assessment, continuity of operations, evaluation planning, and gap analysis—as structured, dedicated sections rather than free-form prose.
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4 }}>
            {capabilities.map((cap, i) => (
                <Paper
                  key={i}
                  elevation={0}
                  sx={{
                    p: 4,
                    width: { xs: '100%', md: 'calc(50% - 16px)' },
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderTop: `4px solid ${BRAND_BLUE}`,
                    transition: 'all 0.3s ease',
                    bgcolor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 15px 35px rgba(15, 76, 156, 0.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'rgba(15, 76, 156, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <cap.icon sx={{ color: BRAND_BLUE, fontSize: 30 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: '#1a1a1a' }}>
                    {cap.title}
                  </Typography>
                  <Typography sx={{ color: '#555', lineHeight: 1.6, fontSize: '1rem' }}>
                    {cap.description}
                  </Typography>
                </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Built for Every Role & Platform Integration */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: 4, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
            Built for Every Role
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', mb: 10 }}>
            {[
              { role: 'Administrators', benefit: 'One auditable assessment record with completion visibility and a documented attestation' },
              { role: 'DONs & clinical leaders', benefit: 'Resident population, acuity, and staffing analysis connected in structured sections' },
              { role: 'QAPI & quality leaders', benefit: 'Gap analysis with mitigation plans and evaluation planning that ties into QAPI' },
              { role: 'Department heads & assignees', benefit: 'Clear assignments with due dates, draft saving, and a simple pending/in-progress queue' },
              { role: 'Corporate / multi-site operators', benefit: 'Consistent assessment structure, cadence, and completion tracking across every facility' }
            ].map((item, i) => (
              <Paper key={i} sx={{ p: 3, width: { xs: '100%', md: 'calc(50% - 12px)' }, border: '1px solid #eaeaea', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1 }}>{item.role}</Typography>
                <Typography sx={{ color: '#555' }}>{item.benefit}</Typography>
              </Paper>
            ))}
          </Box>

          <Typography sx={{ textAlign: 'center', mt: 5, fontWeight: 700, color: BRAND_BLUE, fontSize: '1.1rem' }}>
          </Typography>
        </Container>
      </Box>

      {/* Platform Integration */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: 4, bgcolor: '#ECF3FF' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 3, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            Part of the CareAscendium Platform
          </Typography>
          <Typography sx={{ color: '#555', textAlign: 'center', mb: 6, fontSize: '1.15rem', maxWidth: 800, mx: 'auto' }}>
            Facility Assessment is not a standalone document tool. It connects to the rest of your facility's quality and compliance work:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4 }}>
            {integrations.map((int, i) => (
              <Paper 
                key={i} 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  width: { xs: '100%', md: 'calc(33.333% - 22px)' }, 
                  bgcolor: 'white', 
                  borderRadius: 4, 
                  textAlign: 'left',
                  border: '1px solid rgba(21, 60, 127, 0.1)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 30px rgba(21, 60, 127, 0.15)',
                    borderColor: BRAND_BLUE
                  }
                }}
              >
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: -20, 
                    right: -20, 
                    width: 100, 
                    height: 100, 
                    bgcolor: 'rgba(21, 60, 127, 0.04)', 
                    borderRadius: '50%',
                    zIndex: 0
                  }} 
                />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: BRAND_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, boxShadow: '0 4px 10px rgba(21, 60, 127, 0.3)' }}>
                    <int.icon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5, color: '#1a1a1a' }}>{int.title}</Typography>
                  <Typography sx={{ color: '#555', fontSize: '0.95rem', lineHeight: 1.6 }}>{int.desc}</Typography>
                </Box>
              </Paper>
            ))}
          </Box>
          <Typography sx={{ textAlign: 'center', mt: 6, fontWeight: 700, color: BRAND_BLUE, fontSize: '1.2rem' }}>
            One platform. One source of truth. A facility assessment that actually reflects your facility.
          </Typography>
        </Container>
      </Box>

      {/* Measurable Outcomes & Documentation */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: 4, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
            Outcomes You Can Measure
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', mb: 10 }}>
            {[
              { title: 'Completion visibility', desc: 'Section-level progress on a live dashboard, not a year-end scramble' },
              { title: 'Clear ownership', desc: 'Every assessment assigned to a user or role with a due date' },
              { title: 'On-cadence reviews', desc: 'Quarterly and annual assessments auto-generated on schedule' },
              { title: 'Documented gaps', desc: 'Gaps captured with mitigation plans, owners, and timelines' },
              { title: 'Leadership accountability', desc: 'Dated attestation records with reviewer comments' },
              { title: 'Survey readiness', desc: 'Structured, current documentation across every CMS-expected domain' }
            ].map((outcome, i) => (
              <Box key={i} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' }, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <CheckCircle sx={{ color: BRAND_GREEN }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>{outcome.title}</Typography>
                  <Typography sx={{ color: '#666', fontSize: '0.95rem' }}>{outcome.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Paper sx={{ p: { xs: 4, md: 6 }, bgcolor: BRAND_BLUE, color: 'white', borderRadius: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, textAlign: 'center' }}>
              Documentation You Can Stand Behind
            </Typography>
            <Typography sx={{ textAlign: 'center', mb: 4, fontSize: '1.1rem', opacity: 0.9 }}>
              CareAscendium supports the rigor CMS expects around the facility assessment requirement:
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              {[
                "Structured domains covering resident population, staffing, resources, services, risk, and continuity of operations",
                "CMS identifiers—CCN, NPI, certified beds, licensure—captured in the facility profile",
                "Gap analysis with prioritization, mitigation plans, and closure tracking",
                "Evaluation planning with annual review dates, interim triggers, and QAPI integration",
                "Leadership attestation with comments and dates for a provable sign-off trail",
                "Recurring cadence and a complete assessment history with audit trail"
              ].map((point, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Assessment sx={{ color: BRAND_GREEN, mt: 0.5 }} />
                  <Typography sx={{ fontSize: '1.05rem', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion 
                key={i}
                expanded={expanded === i} 
                onChange={handleChange(i)}
                sx={{ 
                  mb: 2.5, 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8, fontSize: '1.05rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 4 } }}>
        <Box 
          sx={{ 
            py: { xs: 8, md: 10 }, 
            px: 4, 
            position: 'relative', 
            overflow: 'hidden',
            borderRadius: 4,
            maxWidth: 1200,
            mx: 'auto',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: BRAND_BLUE, zIndex: 0 }} />
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              opacity: 0.1, 
              backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)', 
              backgroundSize: '40px 40px', 
              zIndex: 1 
            }} 
          />
          
          <Box sx={{ maxWidth: 900, mx: 'auto', position: 'relative', zIndex: 2, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 3, fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
              Ready to Get Your Facility Assessment Out of the Binder?
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 5, fontSize: { xs: '1.05rem', md: '1.2rem' }, lineHeight: 1.6, maxWidth: 800, mx: 'auto' }}>
              See how CareAscendium helps SNFs complete, maintain, and attest a facility assessment that reflects the residents you serve and the risks you manage—on cadence, with proof.
            </Typography>
            <Box>
              <Link href="/contact" style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: BRAND_GREEN,
                    color: 'white',
                    px: 5,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: '6px',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#55901d' },
                  }}
                >
                  REQUEST A DEMO
                </Button>
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}
